import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

// Tasks
export const getTasks = () => api.get('/tasks');
export const getTask = (id) => api.get(`/tasks/${id}`);
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const completeTask = (id, body = {}) => api.post(`/tasks/${id}/complete`, body);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);

// Projects
export const getProjects = (params) => api.get('/projects', { params });
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const markMaterialPurchased = (projectId, index) => api.patch(`/projects/${projectId}/materials/${index}/purchase`);
export const saveProjectAsTemplate = (id, data) => api.post(`/projects/${id}/save-as-template`, data);
export const createProjectFromTemplate = (id, data) => api.post(`/projects/from-template/${id}`, data);
export const getTemplates = () => api.get('/projects', { params: { templates: true } });

// Rooms
export const getRooms = (params) => api.get('/rooms', { params });
export const getRoom = (id) => api.get(`/rooms/${id}`);
export const createRoom = (data) => api.post('/rooms', data);
export const updateRoom = (id, data) => api.put(`/rooms/${id}`, data);
export const deleteRoom = (id) => api.delete(`/rooms/${id}`);

// Documents
export const getDocuments = (params) => api.get('/documents', { params });
export const uploadDocument = (formData) => api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteDocument = (id) => api.delete(`/documents/${id}`);
export const getDocumentFileUrl = (id) => `/api/documents/${id}/file`;

// Search
export const search = (q) => api.get('/search', { params: { q } });

// History
export const getHistory = (params) => api.get('/history', { params });
export const deleteHistory = (id) => api.delete(`/history/${id}`);

// Expenses
export const getExpenses = (params) => api.get('/expenses', { params });
export const createExpense = (data) => api.post('/expenses', data);
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data);
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);

// Contacts
export const getContacts = () => api.get('/contacts');
export const getContact = (id) => api.get(`/contacts/${id}`);
export const createContact = (data) => api.post('/contacts', data);
export const updateContact = (id, data) => api.put(`/contacts/${id}`, data);
export const deleteContact = (id) => api.delete(`/contacts/${id}`);

// Project Intake (Claude AI)
export const sendIntakeMessage = (messages) => api.post('/intake/message', { messages });
export const sendRescope = (projectId, messages) => api.post('/intake/rescope', { projectId, messages });

// Home profile
export const getHome = () => api.get('/home');
export const updateHome = (data) => api.put('/home', data);

// Rooms — PDF export
export const getRoomPdf = (id) => api.get(`/rooms/${id}/pdf`, { responseType: 'blob' }).then((r) => r.data);

// Home report PDF
export const getHomePdf = () => api.get('/export/home-pdf', { responseType: 'blob' }).then((r) => r.data);

// Consumables
export const getConsumables = (params) => api.get('/consumables', { params });
export const getConsumable = (id) => api.get(`/consumables/${id}`);
export const createConsumable = (data) => api.post('/consumables', data);
export const updateConsumable = (id, data) => api.put(`/consumables/${id}`, data);
export const deleteConsumable = (id) => api.delete(`/consumables/${id}`);
export const useConsumable = (id, qty = 1) => api.post(`/consumables/${id}/use`, { qty });
export const restockConsumable = (id, quantity) => api.post(`/consumables/${id}/restock`, { quantity });

// AI task suggestions
export const getTaskSuggestions = () => api.post('/intake/task-suggestions');

// AI split compound material into individual items
export const splitMaterial = (materialName, projectTitle) =>
  api.post('/intake/split-material', { materialName, projectTitle });

// YouTube tutorials for a project
export const getProjectVideos = (id) => api.get(`/projects/${id}/videos`);

// Affiliate shop config + UPC lookup
export const getShopConfig = () => api.get('/products/config');
export const lookupUpc = (upc) => api.get('/products/lookup', { params: { upc } });

// Dashboard
export const getDashboard = () => api.get('/dashboard');

// Push notifications
export const getVapidKey = () => api.get('/push/vapid-key');
export const subscribePush = (subscription) => api.post('/push/subscribe', subscription);
export const unsubscribePush = (endpoint) => api.delete('/push/unsubscribe', { data: { endpoint } });
export const subscribeApns = (deviceToken) => api.post('/push/subscribe-apns', { deviceToken });
export const unsubscribeApns = (deviceToken) => api.delete('/push/unsubscribe-apns', { data: { deviceToken } });

// Homes management
export const getHomes = () => api.get('/homes');
export const createHome = (data) => api.post('/homes', data);
export const updateHomeName = (id, data) => api.put(`/homes/${id}`, data);
export const switchHome = (id) => api.post(`/homes/switch/${id}`);
export const inviteToHome = (id, email) => api.post(`/homes/${id}/invite`, { email });
export const revokeInvite = (id, token) => api.delete(`/homes/${id}/invite/${token}`);
export const joinHomeByToken = (token) => api.post(`/homes/join-by-token/${token}`);
export const removeMember = (homeId, userId) => api.delete(`/homes/${homeId}/members/${userId}`);

// Tools inventory
export const getTools = (params) => api.get('/tools', { params });
export const getTool = (id) => api.get(`/tools/${id}`);
export const createTool = (data) => api.post('/tools', data);
export const updateTool = (id, data) => api.put(`/tools/${id}`, data);
export const deleteTool = (id) => api.delete(`/tools/${id}`);

// Feature requests
export const getFeatureRequests = () => api.get('/feature-requests');
export const createFeatureRequest = (data) => api.post('/feature-requests', data);
export const deleteFeatureRequest = (id) => api.delete(`/feature-requests/${id}`);
export const getAdminFeatureRequests = () => api.get('/admin/feature-requests');
export const updateAdminFeatureRequest = (id, data) => api.patch(`/admin/feature-requests/${id}`, data);
export const deleteAdminFeatureRequest = (id) => api.delete(`/admin/feature-requests/${id}`);

// Admin console (system admin only)
export const getAdminMetrics = () => api.get('/admin/metrics');
export const getAdminUsers = () => api.get('/admin/users');
export const setUserDisabled = (id, disabled) => api.patch(`/admin/users/${id}/disabled`, { disabled });
export const deleteAdminUser = (id) => api.delete(`/admin/users/${id}`);
export const getAdminDocuments = () => api.get('/admin/documents');
export const deleteAdminDocument = (id) => api.delete(`/admin/documents/${id}`);

// Appliances
export const getAppliances = () => api.get('/appliances');
export const getAppliance = (id) => api.get(`/appliances/${id}`);
export const createAppliance = (data) => api.post('/appliances', data);
export const updateAppliance = (id, data) => api.put(`/appliances/${id}`, data);
export const deleteAppliance = (id) => api.delete(`/appliances/${id}`);
export const addServiceRecord = (id, data) => api.post(`/appliances/${id}/service`, data);
export const deleteServiceRecord = (id, serviceId) => api.delete(`/appliances/${id}/service/${serviceId}`);
